/**
 * @file bits/arcade_url.ts
 * @description Client-side utilities for detecting and parsing Padlet arcade game URLs,
 * mirroring the server-side ArcadeResolver so the frontend can convert a pasted
 * arcade link into a full arcade attachment (type='arcade') that triggers the
 * JWT embed-auth handshake in expanded view.
 */

import { getArcadeGameTypeLabel } from '@@/bits/arcade_game_type_labels'
import { memoizedArvoFetchLink } from '@@/bits/beethoven'
import { captureException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { getPadletTaUrl } from '@@/bits/ta'
import type { AttachmentProps } from '@@/types'

// The wildcard path segment of canonical Arcade share links (/game/<hashid>) — never a real
// activity type. Mirrors Arcade::ResolveWishGameTypeJob::GENERIC_GAME_TYPE on the server.
const GENERIC_ARCADE_GAME_TYPE = 'game'

// Mirrors ArcadeResolver::ARCADE_HOST_REGEX in services/rails/app/services/arcade_resolver.rb
const ARCADE_HOST_REGEX = /^(ta|ta-legacy|arcade)\.padlet\.(com|dev|io|test)$/i
// Mirrors ArcadeResolver::ARCADE_PATH_REGEX — /<activityType>/<hashId>(/play)?
const ARCADE_PATH_REGEX = /^\/([^/]+)\/([^/]+)(\/play)?\/?$/

// Cache-bust suffix for arcade thumbnail screenshot URLs. Mirrors:
// - THUMBNAIL_CACHE_BUST in services/sondheim/src/utils/shared/thumbnailCacheBust.ts
// - Api::ArcadeGamesController::THUMBNAIL_CACHE_BUST in services/rails/app/controllers/api/arcade_games_controller.rb
// Bump all three together to force cached thumbnails to regenerate.
const THUMBNAIL_CACHE_BUST = 60226

/**
 * Builds the live-screenshot thumbnail URL for an arcade game, matching the URL the
 * community picker renders (Api::ArcadeGamesController#community_thumbnail_url) so a
 * pasted game previews identically to a picker-selected one. A pasted link carries no
 * updated_at, so the cache-bust timestamp is 0 — the same fallback the server uses when
 * updated_at is absent.
 */
function buildArcadeThumbnailUrl(gameId: string): string {
  return `${getPadletTaUrl()}/game/${gameId}/thumbnail?key=0-${THUMBNAIL_CACHE_BUST}`
}

/**
 * Returns true if the given URL points to an arcade game activity.
 * Mirrors ArcadeResolver::arcade_link? on the server side.
 */
function isArcadeUrl(url: string): boolean {
  try {
    const { hostname, pathname } = new URL(url)
    return ARCADE_HOST_REGEX.test(hostname) && ARCADE_PATH_REGEX.test(pathname)
  } catch {
    return false
  }
}

interface ParsedArcadeUrl {
  gameType: string
  gameId: string
  /** Normalized base URL — no /play suffix, no query params, no hash. createArcadeEmbedUrl adds ?embed=1 at render time. */
  gameUrl: string
}

function parseArcadeUrl(url: string): ParsedArcadeUrl | null {
  try {
    const urlObj = new URL(url)
    if (!ARCADE_HOST_REGEX.test(urlObj.hostname)) return null
    const match = urlObj.pathname.match(ARCADE_PATH_REGEX)
    if (match == null) return null
    const [, gameType, gameId] = match

    // Normalize to HTTPS: a pasted http:// arcade link would otherwise be stored as the
    // iframe URL and blocked as mixed active content on the HTTPS app, and the JWT
    // embed-auth handshake would fail since ARCADE_ALLOWED_ORIGINS lists only https:// origins.
    urlObj.protocol = 'https:'
    urlObj.pathname = `/${gameType}/${gameId}`
    urlObj.search = ''
    urlObj.hash = ''

    return { gameType, gameId, gameUrl: urlObj.toString() }
  } catch {
    return null
  }
}

/**
 * Builds arcade attachment_props synchronously from a pasted URL, matching the shape
 * the arcade picker produces (ContentPicker#selectArcadeGame) so a pasted game previews
 * identically in the composer and the wish.
 *
 * The thumbnail is derived deterministically from the game id (same live-screenshot
 * endpoint the community picker uses), so the preview renders immediately without an
 * async round-trip. Only the title is left null here and enriched separately via
 * fetchArcadeGameTitle. Building synchronously lets callers write the draft before any
 * await, so an auto-publishing draft still captures the arcade attachment.
 *
 * Returns null if the URL cannot be parsed as an arcade URL.
 */
function buildArcadeAttachmentProps(url: string): AttachmentProps | null {
  const parsed = parseArcadeUrl(url)
  if (parsed == null) return null

  return {
    type: 'arcade',
    url: parsed.gameUrl,
    arcade_game_id: parsed.gameId,
    arcade_game_url: parsed.gameUrl,
    arcade_game_title: null,
    arcade_game_type: parsed.gameType,
    arcade_game_type_label: getArcadeGameTypeLabel(parsed.gameType) || parsed.gameType,
    arcade_thumbnail_url: buildArcadeThumbnailUrl(parsed.gameId),
  }
}

interface ArcadeDraftPatch {
  /**
   * Always undefined — like the arcade picker (ContentPicker#selectArcadeGame), arcade
   * attachments leave the plain `attachment` URL unset so the composer does not fire a
   * link-preview fetch. The normalized game URL lives in wish_content.attachment_props
   * (url / arcade_game_url).
   */
  attachment: undefined
  wish_content: { attachment_props: AttachmentProps; is_processed: boolean }
}

/**
 * Builds the draft/post patch for a pasted or dropped link: full arcade attachment_props
 * (matching the arcade picker) when the link is an arcade game URL, otherwise null so the
 * caller keeps its existing plain-link write. Gated by arcade detection, so non-arcade
 * links are completely unaffected.
 *
 * Also gated by the arcadeAttachment flip: when it is off, the arcade attachment type is
 * unsupported (the picker and content-safety settings hide it, and the embed-auth endpoint
 * returns 403), so a pasted arcade link must stay a plain link rather than become a broken
 * arcade attachment. Returns null when the flip is off so the caller keeps its plain write.
 *
 * Use this for the new-draft / new-post entry points where there is no active draft to
 * mutate; for the active-draft case use the surface drafts store's
 * applyArcadeLinkToActiveDraft, which also enriches the title asynchronously.
 */
function buildArcadeDraftPatch(link: string, altText?: string | null): ArcadeDraftPatch | null {
  if (!isAppUsing('arcadeAttachment')) return null

  const arcadeProps = buildArcadeAttachmentProps(link)
  if (arcadeProps == null) return null

  return {
    attachment: undefined,
    wish_content: {
      attachment_props: altText != null ? { ...arcadeProps, alternative_text: altText } : arcadeProps,
      is_processed: false,
    },
  }
}

/**
 * Fetches the display-only title for an arcade URL from the link API.
 *
 * The thumbnail is derived synchronously in buildArcadeAttachmentProps, so this only
 * enriches the title. Non-fatal by design: returns null on a non-arcade URL or a fetch
 * error so the caller keeps the synchronously built attachment (the card still shows the
 * thumbnail and game type, and auth still works without a title).
 */
async function fetchArcadeGameTitle(url: string): Promise<Pick<AttachmentProps, 'arcade_game_title'> | null> {
  if (parseArcadeUrl(url) == null) return null
  try {
    const response = await memoizedArvoFetchLink(url)
    const data = Array.isArray(response.data) ? response.data[0] : response.data
    const title = data?.attributes?.title ?? null
    if (title == null) return null
    return { arcade_game_title: title }
  } catch {
    // Non-fatal: the title will be absent from the card but the thumbnail and auth still work.
    return null
  }
}

/**
 * Fetches the real activity type for a game whose pasted URL carried only the wildcard /game/
 * path segment, via the Rails proxy of Sondheim's public prompt lookup
 * (GET /api/1/arcade/games/:hashid). Lets the composer preview swap the generic "Arcade game"
 * label for the real type right after paste, instead of waiting for publish +
 * Arcade::ResolveWishGameTypeJob (which remains the backstop for every other write path).
 *
 * Non-fatal by design: returns null when the game can't be resolved (private/deleted game,
 * flip off, upstream failure) so the caller keeps the generic label.
 */
async function fetchArcadeGameTypeProps(
  gameId: string,
): Promise<Pick<AttachmentProps, 'arcade_game_type' | 'arcade_game_type_label'> | null> {
  try {
    const response = await fetch(`/api/1/arcade/games/${encodeURIComponent(gameId)}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
    if (!response.ok) return null
    const json = await response.json()
    const gameType: unknown = json?.data?.game_type
    if (typeof gameType !== 'string' || gameType === '' || gameType === GENERIC_ARCADE_GAME_TYPE) return null
    const serverLabel: unknown = json?.data?.game_type_label
    return {
      arcade_game_type: gameType,
      // Same fallback order as the picker (ContentPicker#selectArcadeGame): prefer the local
      // viewer-locale label for a known slug; the persisted label is only the export/unknown-slug
      // fallback, so the server's (English) label fills it for slugs the local map doesn't know.
      arcade_game_type_label:
        getArcadeGameTypeLabel(gameType) ||
        (typeof serverLabel === 'string' && serverLabel !== '' ? serverLabel : gameType),
    }
  } catch (error) {
    // Non-fatal (the chiclet keeps the generic label), but still worth tracking.
    captureException(error, { tags: { source: 'fetchArcadeGameTypeProps' } })
    return null
  }
}

export {
  buildArcadeAttachmentProps,
  buildArcadeDraftPatch,
  buildArcadeThumbnailUrl,
  fetchArcadeGameTitle,
  fetchArcadeGameTypeProps,
  GENERIC_ARCADE_GAME_TYPE,
  isArcadeUrl,
}
